import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertTriangle, ArrowLeft, BookOpen } from 'lucide-react';
import ManualLeitor from '../ManualLeitor';
import { COR, COR_ESCURA, COR_CLARA } from '../BlocosManual';
import montarManualPortal from './montarManualPortal';

// ============================================================================
// BIBLIOTECA › MANUAIS — o Manual do Portal (DEM-00022, tarefa 14)
// ============================================================================
// Diferente dos manuais estáticos (`conteudo/*.js`), o Manual do Portal é VIRTUAL:
// busca a curadoria já agrupada por módulo (GET /api/biblioteca/conecta/portal),
// monta o objeto `manual` (montarManualPortal — cada módulo = 1 capítulo) e injeta
// no MESMO ManualLeitor, com o token apontando para o proxy de imagem do portal.
//
// ⚠️ Manual VAZIO (nenhum comunicado curado ainda) NÃO entra no ManualLeitor: ele
//    assume `capitulos[0]` na montagem. Aqui trata-se o vazio com um aviso próprio.
// ============================================================================

const FONTE = "'Raleway', sans-serif";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const ManualPortalLeitor = ({ isMobile, onVoltar }) => {
  const [estado, setEstado] = useState({ fase: 'carregando', manual: null });

  const carregar = useCallback(async () => {
    setEstado({ fase: 'carregando', manual: null });
    try {
      const r = await fetch('/api/biblioteca/conecta/portal', { headers: auth() });
      if (!r.ok) throw new Error(String(r.status));
      const payload = await r.json();
      const manual = montarManualPortal(payload);
      setEstado({ fase: manual.capitulos.length === 0 ? 'vazio' : 'pronto', manual });
    } catch {
      setEstado({ fase: 'erro', manual: null });
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const Voltar = () => (
    <button
      type="button"
      onClick={onVoltar}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
        padding: '6px 12px', borderRadius: 8, border: `1px solid ${COR}44`,
        background: COR_CLARA, color: COR_ESCURA, fontFamily: FONTE, fontSize: 13,
        fontWeight: 700, cursor: 'pointer',
      }}
    >
      <ArrowLeft size={14} /> Todos os manuais
    </button>
  );

  if (estado.fase === 'carregando') {
    return (
      <div>
        <Voltar />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#78716c', padding: 24 }}>
          <Loader2 size={18} className="animate-spin" /> Montando o Manual do Portal…
        </div>
      </div>
    );
  }

  if (estado.fase === 'erro') {
    return (
      <div>
        <Voltar />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 16, borderRadius: 12,
          background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c',
        }}>
          <AlertTriangle size={18} /> Não foi possível montar o Manual do Portal.
          <button type="button" onClick={carregar} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#b91c1c', fontWeight: 800, cursor: 'pointer' }}>
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (estado.fase === 'vazio') {
    return (
      <div>
        <Voltar />
        <div style={{
          display: 'flex', gap: 14, alignItems: 'flex-start', padding: '18px 20px', borderRadius: 12,
          background: '#fafaf9', border: '1px dashed #d6d3d1',
        }}>
          <BookOpen size={22} style={{ color: COR, flexShrink: 0, marginTop: 2 }} />
          <div>
            <h3 style={{ margin: '0 0 4px', fontFamily: FONTE, fontSize: 16, fontWeight: 800, color: '#1c1917' }}>
              O Manual do Portal ainda está sendo montado
            </h3>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: '#78716c' }}>
              Ele reúne, por módulo, os comunicados do Conecta marcados como referência do portal.
              A curadoria é feita na aba Conecta (Marketing/Diretoria/Admin).
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ManualLeitor
      manual={estado.manual}
      onVoltar={onVoltar}
      isMobile={isMobile}
      tokenUrl="/api/biblioteca/conecta/portal/token"
      rotuloVoltar="Todos os manuais"
    />
  );
};

export default ManualPortalLeitor;
